/**
 * API client for same-domain communication with the Pharmacy Clinical Copilot backend.
 * Uses /api/* paths through CloudFront routing to ALB.
 * Stage-agnostic - works with any environment through relative URLs.
 */

import { breadcrumb } from '../utils/diagnostics';

const API_BASE_URL = '/api';

// Default per-request timeout. Without this, a fetch that never settles
// (dropped connection during a token/session renewal, network transition,
// laptop sleep/resume) hangs forever. Callers that `await` such a request
// in a loop — notably the shoulder-tap poll loop — would wedge permanently
// with no way to recover. The timeout guards time-to-response-headers, so
// streaming endpoints are unaffected once headers arrive.
//
// Kept well under the poll cadence (15s): missing a single poll is cheap,
// but a request that hung near the interval would push the whole loop
// permanently behind, so we abort early and let the next tick fire on time.
const DEFAULT_TIMEOUT_MS = 5_000;

type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  accept?: string;
  timeoutMs?: number;
};

/**
 * Auth-loss detection.
 *
 * The ALB owns authentication and answers unauthenticated requests with a 302
 * to the IdP (`OnUnauthenticatedRequest: authenticate`), not a 401. With the
 * default `redirect: 'follow'` the browser chases that redirect cross-origin
 * and the failure surfaces as an opaque TypeError — indistinguishable from a
 * 500, a DNS failure, or our own abort timeout. That is exactly what made a
 * session expiry look like "the service is offline".
 *
 * `redirect: 'manual'` (below) stops the chase and hands back an
 * opaque-redirect response instead, which is an unambiguous "the ALB wants to
 * re-authenticate me" signal. These endpoints never legitimately redirect, so
 * nothing is lost.
 *
 * Listeners are notified once per auth-loss episode rather than once per failed
 * request: the shoulder-tap loop polls every few seconds, and every one of
 * those polls fails for the same reason. The latch resets on the next
 * successful request so a later expiry is reported again.
 */
type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();
let unauthenticatedReported = false;

/**
 * Subscribe to auth-loss detected on an API call. Returns an unsubscribe fn.
 *
 * Intended for exactly one consumer (`useUser`), which re-checks `/me` and
 * lets the existing popup flow mint a fresh session cookie.
 */
export const onUnauthenticated = (listener: UnauthenticatedListener): (() => void) => {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
};

const reportUnauthenticated = (path: string, signal: string): void => {
  breadcrumb('auth.unauthenticated', { path, signal, latched: unauthenticatedReported });
  if (unauthenticatedReported) return;
  unauthenticatedReported = true;
  console.warn(`[auth] ${signal} on ${path} — session appears to have expired`);
  unauthenticatedListeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      // A throwing listener must not break the request path.
      console.error('[auth] unauthenticated listener threw:', error);
    }
  });
};

/** Any authenticated response proves the session is live; re-arm the latch. */
const reportAuthenticated = (): void => {
  unauthenticatedReported = false;
};

/**
 * Classify a response as an auth failure. Covers both shapes the ALB can
 * produce: the redirect (normal config) and a bare 401/403 (if a listener rule
 * is ever switched to `OnUnauthenticatedRequest: deny`).
 */
const authFailureSignal = (response: Response): string | null => {
  if (response.type === 'opaqueredirect') return 'redirect-to-idp';
  if (response.status === 401 || response.status === 403) return `HTTP ${response.status}`;
  return null;
};

async function apiRequest(
  path: string,
  {
    method = 'POST',
    body,
    accept = 'application/json',
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }: ApiRequestOptions = {},
): Promise<Response | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      // See the auth-loss detection note above: surface the ALB's auth
      // redirect as an opaque-redirect response instead of chasing it
      // cross-origin and losing the reason for the failure.
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        'Accept': accept,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
    const authSignal = authFailureSignal(response);
    if (authSignal) {
      reportUnauthenticated(path, authSignal);
      throw new Error(`Unauthenticated: ${authSignal}`);
    }
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    reportAuthenticated();
    return response;
  } catch (error) {
    console.error(`Error in ${method} ${path}:`, error);
    return null;
  } finally {
    // Clear as soon as headers are in (or the request failed/aborted) so we
    // never abort a response body mid-stream, and so the timer can't leak.
    clearTimeout(timeoutId);
  }
}

async function apiRequestJson<T>(
  path: string,
  options?: ApiRequestOptions,
): Promise<T | null> {
  const response = await apiRequest(path, options);
  if (!response) return null;
  try {
    return (await response.json()) as T;
  } catch (error) {
    console.error(`Error parsing JSON from ${path}:`, error);
    return null;
  }
}

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  posixGroups: string[];
  cognitoGroups: string[];
  userId?: string;
  provider?: string;
  authenticated: boolean;
  headers: Record<string, string>;
  cookies: Record<string, string>;
  timestamp: string;
}

/** Canonical "not signed in" shape, used for every unauthenticated outcome. */
const UNAUTHENTICATED_USER = (): UserInfo => ({
  authenticated: false,
  firstName: undefined,
  lastName: undefined,
  email: undefined,
  username: undefined,
  posixGroups: [],
  cognitoGroups: [],
  userId: undefined,
  provider: undefined,
  headers: {},
  cookies: {},
  timestamp: new Date().toISOString(),
});

export interface WebSocketCredentials {  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  websocketUrl: string;
  region: string;
}

export interface FeedbackData {
  session_id: string | null;
  prompt: string;
  response: string;
  is_helpful: boolean;
  notes?: string | null;
  not_helpful_reason?: string | null;
}

export interface FeedbackResponse {
  success: boolean;
  message?: string;
}

export interface ShoulderTapConfiguration {
  enabled: boolean;
  poll_interval_seconds: number;
}

export interface ShoulderTapPayload {
  message: string;
  reason: string;
}

/** One tap in the contact's active lifecycle, as returned in `taps`. */
export interface ShoulderTapItem extends ShoulderTapPayload {
  /** DDB sort key. Stable per tap, safe as a list key. */
  id: string;
}

export interface ShoulderTapAnalysis {
  contact_id: string;
  /**
   * @deprecated in favour of `taps`. Only populated on the poll that surfaces a
   * tap — which is why it can't carry the sequence. Still the fire trigger.
   */
  tap: ShoulderTapPayload | null;
  /**
   * Every tap in the contact's active lifecycle — all taps since the last
   * dismiss — oldest first, returned on every poll including non-surfacing
   * ones. `[]` after a dismiss. An accept does NOT end the lifecycle: taps
   * keep accumulating, which is why copy can change after an accept.
   */
  taps: ShoulderTapItem[];
  /**
   * True once any tap in the active lifecycle has been accepted. Contact-
   * scoped, not per-tap.
   */
  accepted: boolean;
}

export interface ShoulderTapAckResponse {
  status: string;
}

export const api = {
  /**
   * Get current user information from the /me endpoint.
   * Returns user info if authenticated, or unauthenticated state if not.
   */
  async getMe(): Promise<UserInfo | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        credentials: 'include', // Ensure cookies are sent for authentication
        // Deliberately does NOT call reportUnauthenticated: this endpoint is
        // how we *answer* an auth-loss signal, so notifying from here would
        // re-trigger the very check that issued the request.
        redirect: 'manual',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok || response.type === 'opaqueredirect') {
        if (
          response.type === 'opaqueredirect' ||
          response.status === 401 ||
          response.status === 403
        ) {
          console.log('User not authenticated - login required');
          return UNAUTHENTICATED_USER();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const userData = await response.json();
      reportAuthenticated();
      console.log('User authenticated:', userData.username || userData.email);
      return userData;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Return unauthenticated state for any error
      return UNAUTHENTICATED_USER();
    }
  },

  saveFeedback(feedbackData: FeedbackData): Promise<Response | null> {
    return apiRequest('/saveFeedback', { body: feedbackData });
  },

  summarizeCustomerPreviousInteraction(contact_id: string): Promise<Response | null> {
    return apiRequest('/summarizeCustomerPreviousInteraction', {
      body: { contact_id },
      accept: 'text/event-stream',
    });
  },

  getShoulderTapConfiguration(contact_id: string): Promise<ShoulderTapConfiguration | null> {
    return apiRequestJson<ShoulderTapConfiguration>('/shoulder-tap-configuration', {
      body: { contact_id },
    });
  },

  getShoulderTapAnalysis(contact_id: string): Promise<ShoulderTapAnalysis | null> {
    return apiRequestJson<ShoulderTapAnalysis>('/shoulder-tap-analysis', {
      body: { contact_id },
    });
  },

  acceptShoulderTap(contact_id: string): Promise<ShoulderTapAckResponse | null> {
    return apiRequestJson<ShoulderTapAckResponse>('/shoulder-tap-accept', {
      body: { contact_id },
    });
  },

  dismissShoulderTap(contact_id: string): Promise<ShoulderTapAckResponse | null> {
    return apiRequestJson<ShoulderTapAckResponse>('/shoulder-tap-dismiss', {
      body: { contact_id },
    });
  },

  getWebSocketCredentials(): Promise<WebSocketCredentials | null> {
    return apiRequestJson<WebSocketCredentials>('/auth/websocket-credentials', { method: 'GET' });
  },
};
